import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

import { HeroRender } from "@/components/editorial/HeroMaster";
import { EditorialStoryRender } from "@/components/editorial/EditorialStoryMaster";
import { VisualStoryRender } from "@/components/editorial/VisualStoryMaster";
import { RecipeRender } from "@/components/editorial/RecipeMaster";
import { HistoricalEditorialRender } from "@/components/editorial/HistoricalEditorialMaster";
import { MacroSensoryRender } from "@/components/editorial/MacroSensoryMaster";
import { PortraitDialogueRender } from "@/components/editorial/PortraitDialogueMaster";
import { MacroRender } from "@/components/editorial/MacroMaster";
import { ProcessStoryRender } from "@/components/editorial/ProcessStoryMaster";
import { StepByStepRender } from "@/components/editorial/StepByStepMaster";
import { EditorialPauseRender } from "@/components/editorial/EditorialPauseMaster";
import { KnowledgeRender } from "@/components/editorial/KnowledgeMaster";

function useReaderMode() {
  const [mode, setMode] = useState(() => (
    typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
      ? "mobile"
      : "desktop"
  ));

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const update = () => setMode(media.matches ? "mobile" : "desktop");
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, []);

  return mode;
}

function EditorialBlockRender({ block, mode }) {
  switch (block.master_type) {
    case "HERO":
      return <HeroRender block={block} mode={mode} />;
    case "EDITORIAL_STORY":
      return <EditorialStoryRender block={block} mode={mode} publicReader />;
    case "VISUAL_STORY":
      return <VisualStoryRender block={block} mode={mode} />;
    case "RECIPE":
      return <RecipeRender block={block} mode={mode} />;
    case "HISTORICAL_EDITORIAL":
      return <HistoricalEditorialRender block={block} mode={mode} />;
    case "MACRO_SENSORY":
      return <MacroSensoryRender block={block} mode={mode} />;
    case "PORTRAIT_DIALOGUE":
      return <PortraitDialogueRender block={block} mode={mode} />;
    case "MACRO":
      return <MacroRender block={block} mode={mode} />;
    case "PROCESS_STORY":
      return <ProcessStoryRender block={block} mode={mode} />;
    case "STEP_BY_STEP":
      return <StepByStepRender block={block} mode={mode} />;
    case "EDITORIAL_PAUSE":
      return <EditorialPauseRender block={block} mode={mode} />;
    case "KNOWLEDGE":
      return <KnowledgeRender block={block} mode={mode} />;
    default: {
      const c = block.content || {};
      return (
        <section className="mx-auto max-w-3xl px-6 py-10">
          {c.eyebrow && <div className="text-[10px] uppercase tracking-widest text-espresso/50">{c.eyebrow}</div>}
          {c.headline && <h2 className="mt-2 font-serif text-3xl text-espresso">{c.headline}</h2>}
          {c.lead && <p className="mt-4 font-serif text-lg italic text-espresso/70">{c.lead}</p>}
          {c.body && <div className="mt-5 whitespace-pre-wrap text-[15px] leading-7 text-espresso/90">{c.body}</div>}
        </section>
      );
    }
  }
}

export default function EditorialPublicMaterial() {
  const { slug } = useParams();
  const mode = useReaderMode();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/editorial/public/open-library/${slug}`)
      .then(({ data: payload }) => {
        if (!cancelled) {
          setData(payload);
          setNotFound(false);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        if (error?.response?.status === 404) setNotFound(true);
        else toast.error(formatApiError(error));
      });
    return () => { cancelled = true; };
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-cream grid place-items-center px-6">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-espresso">Материал не найден</h1>
          <Link to="/library" className="mt-6 inline-block text-sm underline underline-offset-4 text-espresso">
            Вернуться в Открытую библиотеку
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen bg-cream" data-testid="editorial-public-loading" />;
  }

  const blocks = (data.blocks || []).filter((block) => block.is_visible !== false);
  const material = data.material || {};

  return (
    <div className="min-h-screen bg-cream" data-testid="editorial-public-material">
      <header className="sticky top-0 z-20 border-b border-espresso/10 bg-cream/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1280px] items-center px-5 py-3">
          <Link to="/library"
            className="inline-flex items-center gap-2 text-sm text-espresso/70 hover:text-espresso"
            data-testid="editorial-public-back">
            <ChevronLeft className="h-4 w-4" />
            Открытая библиотека
          </Link>
        </div>
      </header>

      <main className="pb-14">
        {blocks.length === 0 ? (
          <div className="mx-auto max-w-3xl px-6 py-16 text-center">
            <h1 className="font-serif text-3xl text-espresso">{material.title}</h1>
            <p className="mt-3 text-sm text-espresso/60">У опубликованной версии пока нет видимых разворотов.</p>
          </div>
        ) : (
          <div className="divide-y divide-espresso/10">
            {blocks.map((block) => (
              <EditorialBlockRender key={block.id} block={block} mode={mode} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
